/** Progress of a bulk run, for display while it is in flight. */
export interface BulkRunProgress {
  completed: number;
  total: number;
}

export interface BulkRunFailure {
  id: number;
  message: string;
}

export interface BulkRunResult {
  succeeded: number[];
  failed: BulkRunFailure[];
}

export interface BulkRunOptions {
  onProgress?: (progress: BulkRunProgress) => void;
  concurrency?: number;
}

/**
 * Jobs in flight at once. Bounded so a large selection can't open hundreds of
 * sockets, but high enough that 100 records don't crawl through serially.
 */
export const BULK_RUN_CONCURRENCY = 5;

/**
 * Applies an operation to many records, reporting progress as each finishes.
 *
 * Two properties the plain `for (const id of ids) await fn(id)` loop this
 * replaces did not have:
 *
 * - **One failure never stops the rest.** Aborting on the first error leaves
 *   the remaining records silently unattempted, and the user with no idea how
 *   far it got. Every record is attempted and the failures are named.
 * - **Bounded parallelism.** 100 records serially is 100 round trips end to
 *   end; the caller still gets a completed count per record either way.
 */
/**
 * Runs `fn` over every item with a bounded number in flight, preserving input
 * order in the result. `fn` owns its own error handling — anything it throws
 * propagates and rejects the whole map.
 *
 * The pool primitive, shared so there is one implementation of "N at a time":
 * {@link runBulk} layers failure collection and progress on top for job writes,
 * and name resolution uses it directly.
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  fn: (item: T, index: number) => Promise<R>,
  concurrency: number = BULK_RUN_CONCURRENCY
): Promise<R[]> => {
  // A zero or negative limit would spawn no workers and return an empty result
  // as though every item had been handled.
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError(`concurrency must be a positive integer, got ${concurrency}`);
  }

  const results = new Array<R>(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      results[index] = await fn(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => worker()));

  return results;
};

export const runBulk = async (
  ids: readonly number[],
  apply: (id: number) => Promise<unknown>,
  { onProgress, concurrency = BULK_RUN_CONCURRENCY }: BulkRunOptions = {}
): Promise<BulkRunResult> => {
  const succeeded: number[] = [];
  const failed: BulkRunFailure[] = [];
  let completed = 0;

  await mapWithConcurrency(
    ids,
    async (id) => {
      try {
        await apply(id);
        succeeded.push(id);
      } catch (err) {
        failed.push({ id, message: err instanceof Error ? err.message : 'Update failed' });
      }
      completed += 1;
      onProgress?.({ completed, total: ids.length });
    },
    concurrency
  );

  return { succeeded, failed };
};

/** Summarises a run for display. Null when everything succeeded. */
export const bulkRunErrorMessage = (result: BulkRunResult, verb = 'updated'): string | null => {
  if (result.failed.length === 0) return null;
  const total = result.succeeded.length + result.failed.length;
  return `${result.succeeded.length} of ${total} job(s) ${verb}. ${result.failed.length} failed.`;
};
